import { supabase, requireOrganizationId } from "./supabase";
import type { Json } from "../types/database.types";
import {
  type PaymentAction,
  type PaymentLineItem,
} from "./paymentLineItems";
import { logWarn } from "./logError";

type PaymentVersionType = "payment" | "revert";

export interface PaymentVersionRow {
  id: string;
  report_id: string;
  version_number: number;
  snapshot: Record<string, unknown>;
  changes_summary: string;
  changed_by: string | null;
  changed_by_name: string | null;
  created_at: string | null;
  version_type: string;
  pdf_url: string | null;
}

export interface PaymentVersionReport {
  id: string;
  project_id?: string | null;
  claim_number?: number | null;
  claim_month?: number | null;
  claim_year?: number | null;
  paid_by_builder?: number | null;
  calculated_payment_amount?: number | null;
  difference?: number | null;
  date_paid?: string | null;
  days_before_due?: number | null;
  payment_due_date?: string | null;
  retention_pct?: number | null;
  retention_amount?: number | null;
  retention_release_amount?: number | null;
  retention_release_gst?: number | null;
  retention_release_total_inc_gst?: number | null;
  retention_received_amount?: number | null;
  retention_received_at?: string | null;
  retention_received_notes?: string | null;
  progress_payment_pct?: number | null;
  variation_payments?: unknown;
  has_unresolved_items?: boolean | null;
  total_inc_gst?: number | null;
  total_exc_gst?: number | null;
  pdf_url?: string | null;
}

interface RecordPaymentVersionParams {
  report: PaymentVersionReport;
  lineItems: PaymentLineItem[];
  changesSummary: string;
  versionType?: PaymentVersionType;
}

const PAYMENT_SNAPSHOT_FIELDS = [
  "project_id",
  "claim_number",
  "claim_month",
  "claim_year",
  "paid_by_builder",
  "calculated_payment_amount",
  "difference",
  "date_paid",
  "days_before_due",
  "payment_due_date",
  "retention_pct",
  "retention_amount",
  "retention_release_amount",
  "retention_release_gst",
  "retention_release_total_inc_gst",
  "retention_received_amount",
  "retention_received_at",
  "retention_received_notes",
  "progress_payment_pct",
  "variation_payments",
  "has_unresolved_items",
  "total_inc_gst",
  "total_exc_gst",
  "pdf_url",
] as const;

function parseDateOnly(dateValue: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateValue);
  if (!match) return null;

  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day));
}

export function calculateDaysBeforeDue(
  paymentDate: string | null | undefined,
  dueDate: string | null | undefined
): number | null {
  if (!paymentDate || !dueDate) return null;

  const paid = parseDateOnly(paymentDate);
  const due = parseDateOnly(dueDate);
  if (!paid || !due) return null;

  paid.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  return Math.ceil((due.getTime() - paid.getTime()) / (1000 * 60 * 60 * 24));
}

export function dateOnlyToUtcIso(dateValue: string | null | undefined): string | null {
  if (!dateValue) return null;
  return `${dateValue}T00:00:00.000Z`;
}

export function normalizePaymentLineItemsForSnapshot(
  lineItems: PaymentLineItem[]
): Omit<PaymentLineItem, "id" | "created_at" | "updated_at">[] {
  return lineItems
    .map((item) => ({
      organization_id: item.organization_id ?? requireOrganizationId(),
      report_id: item.report_id,
      line_type: item.line_type,
      subcategory_id: item.subcategory_id ?? null,
      variation_id: item.variation_id ?? null,
      hire_billing_id: item.hire_billing_id ?? null,
      claimed_percentage: Number(item.claimed_percentage ?? 0),
      claimed_amount: Number(item.claimed_amount ?? 0),
      paid_percentage: Number(item.paid_percentage ?? 0),
      paid_amount: Number(item.paid_amount ?? 0),
      action: item.action,
      agreed_amount: item.agreed_amount ?? null,
      notes: item.notes ?? null,
      pushed_to_month: item.pushed_to_month ?? null,
      pushed_to_year: item.pushed_to_year ?? null,
    }))
    .sort((a, b) => {
      const aKey = `${a.line_type}|${a.subcategory_id ?? ""}|${a.variation_id ?? ""}|${a.hire_billing_id ?? ""}`;
      const bKey = `${b.line_type}|${b.subcategory_id ?? ""}|${b.variation_id ?? ""}|${b.hire_billing_id ?? ""}`;
      return aKey.localeCompare(bKey);
    });
}

export function buildPaymentVersionSnapshot(
  report: PaymentVersionReport,
  lineItems: PaymentLineItem[]
): Record<string, unknown> {
  const snapshot: Record<string, unknown> = {
    snapshot_kind: "claim_payment_reconciliation",
    snapshot_version: 4,
  };

  for (const field of PAYMENT_SNAPSHOT_FIELDS) {
    snapshot[field] = report[field] ?? null;
  }

  snapshot.payment_line_items = normalizePaymentLineItemsForSnapshot(lineItems);
  return snapshot;
}

export function stableStringify(value: unknown): string {
  const normalize = (input: unknown): unknown => {
    if (Array.isArray(input)) return input.map(normalize);
    if (!input || typeof input !== "object") return input;

    return Object.keys(input as Record<string, unknown>)
      .sort()
      .reduce<Record<string, unknown>>((acc, key) => {
        acc[key] = normalize((input as Record<string, unknown>)[key]);
        return acc;
      }, {});
  };

  return JSON.stringify(normalize(value));
}

async function getCurrentUserDisplay() {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) throw new Error("Not authenticated");

  const { data: profile } = await supabase
    .from("profiles")
    .select("full_name")
    .eq("id", user.id)
    .single();

  const fullName = (profile as { full_name?: string } | null)?.full_name;
  return {
    userId: user.id,
    displayName: fullName || user.email || "Unknown",
  };
}

export async function recordPaymentVersion({
  report,
  lineItems,
  changesSummary,
  versionType = "payment",
}: RecordPaymentVersionParams): Promise<void> {
  const { userId, displayName } = await getCurrentUserDisplay();
  const { count, error: countError } = await supabase
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- generated DB types lag the live versions table shape.
    .from("claims_report_versions" as any)
    .select("*", { count: "exact", head: true })
    .eq("report_id", report.id);

  if (countError) throw countError;

  const snapshot = buildPaymentVersionSnapshot(report, lineItems);
  const { error } = await supabase
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- generated DB types lag organization_id on this table.
    .from("claims_report_versions" as any)
    .insert({
      organization_id: requireOrganizationId(),
      report_id: report.id,
      version_number: (count || 0) + 1,
      version_type: versionType,
      pdf_url: report.pdf_url || null,
      snapshot: snapshot as Json,
      changes_summary: changesSummary,
      changed_by: userId,
      changed_by_name: displayName,
    });

  if (error) throw error;
}

export async function recordCurrentPaymentVersion(
  reportId: string,
  changesSummary: string,
  versionType: PaymentVersionType = "payment"
): Promise<void> {
  const { data: report, error: reportError } = await supabase
    .from("claims_reports")
    .select("*")
    .eq("id", reportId)
    .single();

  if (reportError) throw reportError;

  const { data: lineItems, error: lineItemsError } = await supabase
    .from("claims_payment_line_items")
    .select("*")
    .eq("report_id", reportId);

  if (lineItemsError) throw lineItemsError;

  await recordPaymentVersion({
    report: report as PaymentVersionReport,
    lineItems: (lineItems as unknown as PaymentLineItem[]) || [],
    changesSummary,
    versionType,
  });
}

export async function fetchPaymentVersions(
  reportId: string
): Promise<PaymentVersionRow[]> {
  const { data, error } = await supabase
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- generated DB types lag the live versions table shape.
    .from("claims_report_versions" as any)
    .select("*")
    .eq("report_id", reportId)
    .in("version_type", ["payment", "revert"])
    .order("version_number", { ascending: false });

  if (error) throw error;
  return ((data as unknown as PaymentVersionRow[]) || []).filter((version) =>
    hasRestorablePaymentSnapshot(version)
  );
}

export function hasRestorablePaymentSnapshot(version: PaymentVersionRow): boolean {
  return Array.isArray(version.snapshot?.payment_line_items);
}

function toPaymentLineItem(
  item: unknown,
  reportId: string
): PaymentLineItem | null {
  if (!item || typeof item !== "object") return null;

  const row = item as Record<string, unknown>;
  const lineType = row.line_type;
  const action = row.action;
  if (
    lineType !== "progress" &&
    lineType !== "variation" &&
    lineType !== "retention" &&
    lineType !== "hire"
  ) {
    return null;
  }
  if (
    action !== "pending" &&
    action !== "paid" &&
    action !== "voided" &&
    action !== "held" &&
    action !== "agreement_made" &&
    action !== "works_not_completed"
  ) {
    return null;
  }

  return {
    organization_id:
      typeof row.organization_id === "string"
        ? row.organization_id
        : requireOrganizationId(),
    report_id: reportId,
    line_type: lineType,
    subcategory_id:
      typeof row.subcategory_id === "string" ? row.subcategory_id : null,
    variation_id:
      typeof row.variation_id === "string" ? row.variation_id : null,
    hire_billing_id:
      typeof row.hire_billing_id === "string" ? row.hire_billing_id : null,
    claimed_percentage: Number(row.claimed_percentage ?? 0),
    claimed_amount: Number(row.claimed_amount ?? 0),
    paid_percentage: Number(row.paid_percentage ?? 0),
    paid_amount: Number(row.paid_amount ?? 0),
    action: action as PaymentAction,
    agreed_amount:
      row.agreed_amount === null || row.agreed_amount === undefined
        ? null
        : Number(row.agreed_amount),
    notes: typeof row.notes === "string" ? row.notes : null,
    pushed_to_month:
      row.pushed_to_month === null || row.pushed_to_month === undefined
        ? null
        : Number(row.pushed_to_month),
    pushed_to_year:
      row.pushed_to_year === null || row.pushed_to_year === undefined
        ? null
        : Number(row.pushed_to_year),
  };
}

function paymentCountsForVariation(action: PaymentAction): boolean {
  return action === "paid" || action === "agreement_made";
}

async function syncVariationStateFromPaymentLines(
  reportId: string,
  currentItems: PaymentLineItem[],
  restoredItems: PaymentLineItem[]
): Promise<void> {
  const variationIds = Array.from(
    new Set(
      [...currentItems, ...restoredItems]
        .map((item) => item.variation_id)
        .filter((id): id is string => Boolean(id))
    )
  );
  if (variationIds.length === 0) return;

  const { data: reportRow } = await supabase
    .from("claims_reports")
    .select("project_id")
    .eq("id", reportId)
    .single();

  const { data: userData } = await supabase.auth.getUser();
  const currentUserId = userData.user?.id ?? null;
  const projectId = (reportRow as { project_id?: string } | null)?.project_id;

  const restoredByVariationId = new Map(
    restoredItems
      .filter((item) => item.line_type === "variation" && item.variation_id)
      .map((item) => [item.variation_id as string, item])
  );

  // Deliberately NOT filtered by dashpivot_removed_at: addressed by an explicit
  // id list from a stored payment version, so restoring recorded history must
  // still reach a variation whose Dashpivot form later vanished.
  const { data: variations, error } = await supabase
    .from("claims_variations")
    .select("id, total, discount_percent, dashpivot_form_id")
    .in("id", variationIds);

  if (error) throw error;

  for (const variation of variations || []) {
    const item = restoredByVariationId.get(variation.id);
    const effectivePaid =
      item && paymentCountsForVariation(item.action) ? Number(item.paid_amount || 0) : 0;
    const total = Number(variation.total ?? 0);
    const discount = Number(variation.discount_percent ?? 0);
    const effective = discount > 0 ? total * (1 - discount / 100) : total;
    const approvedAmount = Math.min(effectivePaid, effective);
    const waitingAmount = Math.max(
      0,
      Math.round((effective - approvedAmount) * 100) / 100
    );
    const reconciliationStatus = item?.action === "paid" ? "paid" : null;

    await supabase
      .from("claims_variations")
      .update({
        approved_amount: approvedAmount,
        waiting_amount: waitingAmount,
        reconciliation_status: reconciliationStatus,
        reconciled_by: reconciliationStatus ? currentUserId : null,
        reconciled_at: reconciliationStatus ? new Date().toISOString() : null,
      })
      .eq("id", variation.id);

    if (variation.dashpivot_form_id && projectId) {
      try {
        await supabase.functions.invoke("push-variation-update", {
          body: {
            dashpivotFormId: variation.dashpivot_form_id,
            approvedAmount: Math.round(approvedAmount * 100) / 100,
            projectId,
          },
        });
      } catch (pushErr) {
        logWarn(`[PaymentVersions] Dashpivot sync failed during payment revert for variation ${variation.id}:`, pushErr);
      }
    }
  }
}

export async function revertToPaymentVersion(
  version: PaymentVersionRow
): Promise<void> {
  if (!hasRestorablePaymentSnapshot(version)) {
    throw new Error("This version does not contain a restorable payment snapshot.");
  }

  const reportId = version.report_id;
  const { data: currentItems, error: currentItemsError } = await supabase
    .from("claims_payment_line_items")
    .select("*")
    .eq("report_id", reportId);

  if (currentItemsError) throw currentItemsError;

  const restoredItems = (version.snapshot.payment_line_items as unknown[])
    .map((item) => toPaymentLineItem(item, reportId))
    .filter((item): item is PaymentLineItem => Boolean(item));

  const { displayName } = await getCurrentUserDisplay();
  const rpc = supabase.rpc as unknown as (
    functionName: string,
    args: Record<string, unknown>
  ) => Promise<{ error: { message: string } | null }>;
  const { error } = await rpc("revert_claim_payment_reconciliation_to_version_v2", {
    p_version_id: version.id,
    p_changed_by_name: displayName,
  });

  if (error) throw new Error(error.message);

  await syncVariationStateFromPaymentLines(
    reportId,
    (currentItems as unknown as PaymentLineItem[]) || [],
    restoredItems
  );
}
